import React, { useState, useRef, Fragment } from "react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { Listbox, Transition } from "@headlessui/react";
import { TagItem } from "./BrowseV2/types/TagItem.interface";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const truncate = (options, showAnything = false) => {
  if (!options || options === null) return "";
  if (options.length === 0) {
    return showAnything ? "Anything 🔍" : "";
  }
  if (options.length === 1) {
    return options[0].name;
  }
  if (options.length === 2) {
    return `${options[0].name}, ${options[1].name}`;
  }
  return `${options[0].name}, ${options[1].name} + ${options.length - 2}`;
};

interface Item {
  id: string;
  name: string;
}

interface Props {
  options: TagItem[];
  label: string;
  selectedItems: TagItem[];
  onChange: (x: TagItem[]) => void;
  showAnything?: boolean;
}

const MultiSelect: React.FC<Props> = ({
  options,
  selectedItems = [],
  label,
  onChange,
  showAnything = false
}) => {
  const [query, setQuery] = useState("");
  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => {
        return option.name.toLowerCase().includes(query.toLowerCase());
      });

  return (
    // @ts-ignore
    <Listbox as="div" value={selectedItems} onChange={onChange} multiple>
      <Listbox.Label className="block text-sm font-medium text-black">
        {label}
      </Listbox.Label>
      <div className="relative mt-1">
        <Listbox.Button
          className="w-full rounded-full border border-gray-300 bg-white py-2 pl-4 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm text-left"
          onChange={(event: any) => setQuery(event.target.value)}
        >
          <span className="block truncate min-h-[20px]">{truncate(selectedItems, showAnything)}</span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </Listbox.Button>
        {filteredOptions.length > 0 && (
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredOptions.map((option) => (
                <Listbox.Option
                  key={option.id}
                  value={option}
                  className={({ active }) =>
                    classNames(
                      "relative cursor-default select-none py-2 pl-8 pr-4",
                      active ? "bg-indigo-600 text-white" : "text-gray-900"
                    )
                  }
                >
                  {({ active }) => {
                    const selected = selectedItems.find(
                      (item) => item.id === option.id
                    );
                    return (
                      <>
                        <span
                          className={classNames(
                            "block truncate",
                            selected && "font-semibold"
                          )}
                        >
                          {option.name}
                        </span>

                        {selected && (
                          <span
                            className={classNames(
                              "absolute inset-y-0 left-0 flex items-center pl-1.5",
                              active ? "text-white" : "text-indigo-600"
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    );
                  }}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        )}
      </div>
    </Listbox>
  );
};

export default MultiSelect;
